import React, { useEffect, useMemo, useState } from "react";
import { isLoading, Loading, loading } from "../../Loading";
import { AgencyId, OrientationLanguages, PassportCategoryId } from "../../messages";
import {
  AgencyDocumentType,
  ExtendedAgencyDocumentCategory,
  ExtendedPassportDocumentCategory,
  PassportDocumentCategories,
  PassportDocumentType,
} from "../../messages/UploadedDocuments";
import { getLocalStorageItem, setLocalStorageItem } from "../../storage_utils";
import { EventSource } from "../../system/EventSource";
import { getLanguage, langMap } from "../../translate";
import { withDefault } from "../../utils/withDefault";
import MedflytPassportScreen from "./MedflytPassportScreen";

export type TermStatus = "LOADING" | "APPROVED" | "PENDING";
export type CompliantStatus = "COMPLIANT" | "INCOMPLIANT" | "MISSING_DOC_TYPE";

export const MEDFLYT_PASSPORT_POPUP_DISCLAIMER = "MEDFLYT_PASSPORT_POPUP_DISCLAIMER";

interface Props {
  onCategoriesUpdated: () => EventSource<void>;
  getMedflytPassportCategories: () => Promise<PassportDocumentCategories>;
  onBackClick: () => void;
  onDocumentTypeClick: (
    agencyId: AgencyId | null,
    documentType: PassportDocumentType | AgencyDocumentType,
    language: OrientationLanguages
  ) => void;
}

const MedflytPassportActivity = ({
  onCategoriesUpdated,
  getMedflytPassportCategories,
  onBackClick,
  onDocumentTypeClick,
}: Props) => {
  const [categories, setCategories] = useState<ExtendedPassportDocumentCategory[] | Loading>(
    loading()
  );
  const [agenciesCategories, setAgenciesCategories] = useState<
    ExtendedAgencyDocumentCategory[] | Loading
  >(loading());
  const [language, setLanguage] = useState<OrientationLanguages | Loading>(loading());
  const [termStatus, setTermStatus] = useState<TermStatus>("LOADING");
  const [, setHasError] = useState<boolean>(false);

  const handleAcceptTerms = useMemo(
    () => async () => {
      await setLocalStorageItem(MEDFLYT_PASSPORT_POPUP_DISCLAIMER, "APPROVED");
      setTermStatus("APPROVED");
    },
    []
  );

  const loadTermsStatus = useMemo(
    () => async () => {
      const terms = await getLocalStorageItem(MEDFLYT_PASSPORT_POPUP_DISCLAIMER);

      switch (terms) {
        case "APPROVED":
          return setTermStatus(terms);
        default:
          return setTermStatus("PENDING");
      }
    },
    []
  );

  const scrollIntoCategoryView = React.useCallback(
    (contentRef: React.RefObject<HTMLDivElement>) => {
      setTimeout(() => {
        if (contentRef.current !== null) {
          contentRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
    },
    []
  );

  const handleClickCategoryHeader = React.useCallback(
    (passportCategoryId: PassportCategoryId, contentRef: React.RefObject<HTMLDivElement>) => {
      if (!isLoading(categories)) {
        const categoryIndex = categories.findIndex(
          (category) => category.id === passportCategoryId
        );
        if (categoryIndex > -1) {
          const updatedCategory = categories[categoryIndex];
          updatedCategory.isOpen = !updatedCategory.isOpen ? true : false;
          const updatedCategories = [...categories];
          updatedCategories[categoryIndex] = updatedCategory;
          setCategories(updatedCategories);

          if (updatedCategory.isOpen) {
            scrollIntoCategoryView(contentRef);
          }
        }
      }
    },
    [categories, scrollIntoCategoryView]
  );

  const handleClickAgencyCategoryHeader = React.useCallback(
    (agencyId: AgencyId, contentRef: React.RefObject<HTMLDivElement>) => {
      if (!isLoading(agenciesCategories)) {
        const agencyCategoryIndex = agenciesCategories.findIndex(
          (agencyCategory) => agencyCategory.agencyId === agencyId
        );
        if (agencyCategoryIndex > -1) {
          const updatedCategory = agenciesCategories[agencyCategoryIndex];
          updatedCategory.isOpen = !updatedCategory.isOpen;
          const updatedAgenciesCategories = [...agenciesCategories];
          updatedAgenciesCategories[agencyCategoryIndex] = updatedCategory;
          setAgenciesCategories(updatedAgenciesCategories);

          if (updatedCategory.isOpen) {
            scrollIntoCategoryView(contentRef);
          }
        }
      }
    },
    [agenciesCategories, scrollIntoCategoryView]
  );

  const loadCategories = useMemo(
    () => () => {
      getMedflytPassportCategories()
        .then((response) => {
          const _agenciesCategories: ExtendedAgencyDocumentCategory[] = [];
          response.agencies.forEach((agencyCategory) => {
            _agenciesCategories.push({ ...agencyCategory, isOpen: false });
          });
          setAgenciesCategories(_agenciesCategories);

          const _categories: ExtendedPassportDocumentCategory[] = [];
          response.categories.forEach((category) => {
            _categories.push({ ...category, isOpen: false });
          });
          setCategories(_categories);
        })
        .catch(() => setHasError(true));
    },
    [getMedflytPassportCategories]
  );

  useEffect(() => {
    getLanguage()
      .then((clientLanguage) => {
        const selectedLanguage = withDefault(langMap[clientLanguage], "English");
        setLanguage(selectedLanguage);
      })
      .catch(() => setHasError(true));
  }, []);

  useEffect(() => {
    loadCategories();
    const listener = onCategoriesUpdated().register(() => {
      loadCategories();
    });
    return () => void listener.unregister();
  }, [onCategoriesUpdated, loadCategories]);

  useEffect(() => {
    loadTermsStatus();
  }, [loadTermsStatus]);

  return (
    <MedflytPassportScreen
      categories={categories}
      agenciesCategories={agenciesCategories}
      language={language}
      termStatus={termStatus}
      onAcceptTerms={handleAcceptTerms}
      onBackClick={onBackClick}
      onClickDocumentType={onDocumentTypeClick}
      onClickCatergoryHeader={handleClickCategoryHeader}
      onClickAgencyCatergoryHeader={handleClickAgencyCategoryHeader}
    />
  );
};

export default MedflytPassportActivity;
